import axios from 'axios';

function _simplifyManifest(manifest) {
  const status = manifest && manifest.status ? manifest.status : 'unknown';
  const raw = manifest && manifest.progress ? String(manifest.progress) : '';
  const progress =
    raw === 'complete' ? 100 : parseInt((raw || '0').replace('%', ''), 10) || 0;
  const has3D = Array.isArray(manifest && manifest.derivatives)
    ? manifest.derivatives.some((d) =>
        String(d.outputType).toLowerCase().includes('svf')
      )
    : false;
  return { status, progress, has3D };
}

function _regionPrefix(region) {
  return region === 'EMEA' ? 'regions/eu/' : '';
}

const AutodeskService = {
  async getModelStatus(urn, token, region = 'US') {
    try {
      const m = await this.getUrnManifest(urn, token, region);
      return _simplifyManifest(m);
    } catch (_) {
      return { status: 'inprogress', progress: 0, has3D: false };
    }
  },

  async pollModelUntilReady(urn, token, region = 'US', opts) {
    const intervalMs = (opts && opts.intervalMs) || 8000;
    const maxAttempts = (opts && opts.maxAttempts) || 60;
    for (let i = 0; i < maxAttempts; i++) {
      const s = await this.getModelStatus(urn, token, region);
      if (s.status === 'success') return s;
      if (s.status === 'failed') throw new Error('Autodesk translation failed');
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    throw new Error('Timeout while waiting for Autodesk translation');
  },

  // --- Auth ---
  async fetchToken(clientId, clientSecret, server_region) {
    this.region = server_region;
    const authHeader = `Basic ${Buffer.from(
      `${clientId}:${clientSecret}`
    ).toString('base64')}`;
    const url = 'https://developer.api.autodesk.com/authentication/v2/token';
    const data = {
      grant_type: 'client_credentials',
      scope:
        'data:read data:write data:create bucket:read bucket:create account:read',
    };
    try {
      const response = await axios({
        method: 'post',
        url,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
          Authorization: authHeader,
        },
        data: new URLSearchParams(data).toString(),
      });
      return response.data.access_token;
    } catch (error) {
      this.$store?.commit?.('showNotification', {
        content: `Autodesk: ${error.message}`,
        color: 'error',
      });
    }
  },

  // --- Metadata / tree / properties ---
  getUrnMetadata(urn, token, region = 'US') {
    const url =
      region === 'EMEA'
        ? `https://developer.api.autodesk.com/modelderivative/v2/regions/eu/designdata/${urn}/metadata`
        : `https://developer.api.autodesk.com/modelderivative/v2/designdata/${urn}/metadata`;
    return new Promise((resolve, reject) => {
      const config = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${token}`,
        },
      };
      axios
        .get(url, config)
        .then((response) => resolve(response.data.data.metadata))
        .catch(reject);
    });
  },

  getGuidMetadata(urn, guid, token) {
    return new Promise((resolve, reject) => {
      const config = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${token}`,
        },
      };
      axios
        .get(
          `https://developer.api.autodesk.com/modelderivative/v2/designdata/${urn}/metadata/${guid}?forceget=true`,
          config
        )
        .then((response) => resolve(response.data.data.objects))
        .catch(reject);
    });
  },

  getGuidProperties(urn, guid, token, region = 'US') {
    const base =
      region === 'EMEA'
        ? 'https://developer.api.autodesk.com/modelderivative/v2/regions/eu/designdata'
        : 'https://developer.api.autodesk.com/modelderivative/v2/designdata';
    const url = `${base}/${urn}/metadata/${guid}/properties?forceget=true`;

    return new Promise((resolve, reject) => {
      axios
        .get(url, {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          validateStatus: () => true,
        })
        .then((response) => {
          const { status, data } = response;

          // READY only if collection exists AND has items
          if (
            status === 200 &&
            data &&
            data.data &&
            Array.isArray(data.data.collection) &&
            data.data.collection.length > 0
          ) {
            return resolve(data.data.collection);
          }

          // 200 + collection present but empty => still indexing
          if (
            status === 200 &&
            data &&
            data.data &&
            Array.isArray(data.data.collection) &&
            data.data.collection.length === 0
          ) {
            const err = new Error('PROPERTIES_NOT_READY');
            err.code = 'PROPERTIES_NOT_READY';
            err.httpStatus = 200;
            return reject(err);
          }

          // 200 + {result:"success"} without collection => still indexing
          if (
            status === 200 &&
            data &&
            data.result === 'success' &&
            (!data.data || !data.data.collection)
          ) {
            const err = new Error('PROPERTIES_NOT_READY');
            err.code = 'PROPERTIES_NOT_READY';
            err.httpStatus = 200;
            return reject(err);
          }

          // Not ready yet (typical while indexing)
          if (status === 202 || status === 404) {
            const err = new Error('PROPERTIES_NOT_READY');
            err.code = 'PROPERTIES_NOT_READY';
            err.httpStatus = status;
            return reject(err);
          }

          // Anything else -> hard error
          const err = new Error(`FAILED_TO_FETCH_PROPERTIES (HTTP ${status})`);
          err.code = 'FAILED_TO_FETCH_PROPERTIES';
          err.httpStatus = status;
          err.payload = data;
          return reject(err);
        })
        .catch((error) => {
          const err = new Error('FAILED_TO_FETCH_PROPERTIES');
          err.code = 'FAILED_TO_FETCH_PROPERTIES';
          err.cause = error;
          return reject(err);
        });
    });
  },

  getGuidTree(urn, guid, token, region = 'US') {
    const url =
      region === 'EMEA'
        ? `https://developer.api.autodesk.com/modelderivative/v2/regions/eu/designdata/${urn}/metadata/${guid}`
        : `https://developer.api.autodesk.com/modelderivative/v2/designdata/${urn}/metadata/${guid}`;
    return new Promise((resolve, reject) => {
      const config = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${token}`,
        },
      };
      axios
        .get(`${url}?forceget=true`, config)
        .then((response) => resolve(response.data.data.objects))
        .catch(reject);
    });
  },

  // Buckets / objects
  getBuckets(token, region) {
    return new Promise((resolve, reject) => {
      const server = region === 'EMEA' ? '?region=EMEA' : '';
      const config = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${token}`,
        },
      };
      axios
        .get(
          `https://developer.api.autodesk.com/oss/v2/buckets${server}`,
          config
        )
        .then((response) => resolve(response.data))
        .catch(reject);
    });
  },

  getFilesInBucket(bucket, token) {
    return new Promise((resolve, reject) => {
      const config = {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${token}`,
        },
      };
      axios
        .get(
          `https://developer.api.autodesk.com/oss/v2/buckets/${bucket}/objects?limit=100`,
          config
        )
        .then((response) => resolve(response.data.items))
        .catch(reject);
    });
  },

  createBucket(token, bucket, region) {
    return new Promise((resolve, reject) => {
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-ads-region': region,
        },
      };
      axios
        .post(
          `https://developer.api.autodesk.com/oss/v2/buckets`,
          bucket,
          config
        )
        .then((response) => resolve(response.data))
        .catch(reject);
    });
  },

  uploadForgeFile(bucket, file, token) {
    const rootFileName = file.name;
    return new Promise((resolve, reject) => {
      const config = {
        headers: {
          'Content-Type': 'text/octet-stream',
          Authorization: `Bearer ${token}`,
        },
      };
      axios
        .put(
          `https://developer.api.autodesk.com/oss/v2/buckets/${bucket}/objects/${rootFileName}`,
          new Blob([file]),
          config
        )
        .then((response) => resolve(response.data))
        .catch(reject);
    });
  },

  convertModel(urn, token, region) {
    return new Promise((resolve, reject) => {
      const server = region === 'EMEA' ? 'regions/eu/' : '';
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      };
      const body = {
        input: { urn },
        output: { formats: [{ type: 'svf', views: ['3D', '2D'] }] },
      };
      axios
        .post(
          `https://developer.api.autodesk.com/modelderivative/v2/${server}designdata/job`,
          body,
          config
        )
        .then((response) => resolve(response.data))
        .catch(reject);
    });
  },

  fetchUrnThumbnail(urn, token, region) {
    return new Promise((resolve, reject) => {
      const server = region === 'EMEA' ? 'regions/eu/' : '';
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      };
      axios
        .get(
          `https://developer.api.autodesk.com/modelderivative/v2/${server}designdata/${urn}/thumbnail`,
          config
        )
        .then((response) => resolve(response.data))
        .catch(reject);
    });
  },

  extractGeometry(urn, token, region, objectIds, modelGuid) {
    return new Promise((resolve, reject) => {
      const server = region === 'EMEA' ? 'regions/eu/' : '';
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      };
      const body = {
        input: { urn },
        output: {
          formats: [{ type: 'obj', advanced: { modelGuid, objectIds } }],
        },
      };
      axios
        .post(
          `https://developer.api.autodesk.com/modelderivative/v2/${server}designdata/job`,
          body,
          config
        )
        .then((response) => resolve(response.data))
        .catch(reject);
    });
  },

  getUrnManifest(urn, token, region) {
    return new Promise((resolve, reject) => {
      const server = region === 'EMEA' ? 'regions/eu/' : '';
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      };
      axios
        .get(
          `https://developer.api.autodesk.com/modelderivative/v2/${server}designdata/${urn}/manifest`,
          config
        )
        .then((response) => resolve(response.data))
        .catch(reject);
    });
  },

  deleteObjectFromBucket(bucketKey, objectKey, token) {
    return new Promise((resolve, reject) => {
      const config = {
        headers: {
          'Content-Type': 'text/octet-stream',
          Authorization: `Bearer ${token}`,
        },
      };
      axios
        .delete(
          `https://developer.api.autodesk.com/oss/v2/buckets/${bucketKey}/objects/${objectKey}`,
          config
        )
        .then((response) => resolve(response.data))
        .catch(reject);
    });
  },

  // High-level helpers
  getModelProperties(urn, token, region) {
    return this.getUrnMetadata(urn, token, region)
      .then(
        (views) =>
          (Array.isArray(views)
            ? views.find((v) => String(v.role).toLowerCase() === '3d')
            : null
          )?.guid
      )
      .then((guid) => {
        if (!guid) return [];
        return this.getGuidProperties(urn, guid, token, region).catch((err) => {
          if (err && err.code === 'PROPERTIES_NOT_READY') return [];
          throw err;
        });
      });
  },

  getModelTree(urn, token, region) {
    return this.getUrnMetadata(urn, token, region)
      .then((data) => data.find((e) => e.role === '3d')?.guid)
      .then((guid) => this.getGuidTree(urn, guid, token, region));
  },

  downloadObjExtraction(urn, token, region, objUrn) {
    return new Promise((resolve, reject) => {
      const server = region === 'EMEA' ? 'regions/eu/' : '';
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      };
      axios
        .get(
          `https://developer.api.autodesk.com/modelderivative/v2/${server}designdata/${urn}/manifest/${objUrn}`,
          config
        )
        .then((response) => resolve(response.data))
        .catch(reject);
    });
  },

  // --- Properties readiness (cards) ---
  async get3DGuid(urn, token, region) {
    const views = await this.getUrnMetadata(urn, token, region);
    const view3d = Array.isArray(views)
      ? views.find((v) => String(v.role).toLowerCase() === '3d')
      : null;
    return view3d ? view3d.guid : null;
  },

  async getPropertiesStatus(urn, guid, token, region) {
    // 1) Probe the exact GUID properties endpoint (freshest)
    const base = `https://developer.api.autodesk.com/modelderivative/v2/${_regionPrefix(region)}`;
    const url = `${base}designdata/${urn}/metadata/${guid}/properties?forceget=true`;
    try {
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        validateStatus: () => true,
      });

      // Ready only if collection exists AND has items
      if (
        res.status === 200 &&
        res.data &&
        res.data.data &&
        Array.isArray(res.data.data.collection)
      ) {
        if (res.data.data.collection.length > 0) {
          return { status: 'success', progress: 100 };
        }
        // present but empty -> still indexing
        return { status: 'inprogress', progress: 0 };
      }

      // 200 + {result:"success"} without collection => still indexing
      if (res.status === 200 && res.data && res.data.result === 'success') {
        return { status: 'inprogress', progress: 0 };
      }

      if (res.status === 202 || res.status === 404) {
        return { status: 'inprogress', progress: 0 };
      }
      if (res.status === 500) {
        return { status: 'failed', progress: 0 };
      }
      // fall through to manifest check
    } catch (_) {
      // ignore and try manifest branch
    }

    // 2) Manifest check restricted to the subtree of the given GUID
    try {
      const manifest = await this.getUrnManifest(urn, token, region);
      const derivatives = Array.isArray(manifest?.derivatives)
        ? manifest.derivatives
        : [];

      // DFS to find the node with this GUID and its derivative root
      let guidNode = null;
      let rootForGuid = null;

      const stack = derivatives.map((d) => ({ node: d, root: d }));
      while (stack.length) {
        const { node, root } = stack.pop();
        if (!node) continue;

        if (node.guid === guid) {
          guidNode = node;
          rootForGuid = root;
          break;
        }

        if (Array.isArray(node.children)) {
          node.children.forEach((child) => stack.push({ node: child, root }));
        }
      }

      // Search only inside that branch for property DB nodes
      if (guidNode || rootForGuid) {
        const start = guidNode || rootForGuid;
        const s = [start];

        while (s.length) {
          const n = s.pop();
          if (!n) continue;

          const role = (n.role || '').toString().toLowerCase();
          const type = (n.type || '').toString().toLowerCase();
          const urnStr = (n.urn || '').toString();
          const looksProp =
            role.indexOf('propertydatabase') >= 0 ||
            type.indexOf('autodesk-db') >= 0 ||
            urnStr.indexOf('/properties') >= 0;

          if (looksProp) {
            const st = (n.status || '').toString().toLowerCase();
            if (st === 'success') return { status: 'success', progress: 100 };
            if (st === 'failed') return { status: 'failed', progress: 0 };
            if (st === 'inprogress') {
              const p =
                n.progress === 'complete'
                  ? 100
                  : parseInt(String(n.progress || '0').replace('%', ''), 10) ||
                    0;
              return { status: 'inprogress', progress: p };
            }
          }

          if (Array.isArray(n.children)) s.push(...n.children);
        }
      }
    } catch (_) {
      // ignore
    }

    return { status: 'unknown', progress: 0 };
  },

  async pollPropertiesUntilReady(urn, guid, token, region, opts) {
    const intervalMs = (opts && opts.intervalMs) || 8000;
    const maxAttempts = (opts && opts.maxAttempts) || 60;
    for (let i = 0; i < maxAttempts; i++) {
      const s = await this.getPropertiesStatus(urn, guid, token, region);
      if (s.status === 'success') return s;
      if (s.status === 'failed')
        throw new Error('Autodesk properties build failed');
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    throw new Error('Timeout while waiting for Autodesk properties');
  },
};

export default AutodeskService;
